import { MagnifyingGlassIcon, MapPinIcon } from "@heroicons/react/24/solid";
import cx from "classnames";
import { PageProps } from "gatsby";
import React, { useState } from "react";
import { UseFormHandleSubmit, UseFormRegister } from "react-hook-form";
import type { AllowedSearchParams } from "../../hooks/useFormValuesFromURL";
import useFormValuesFromURL from "../../hooks/useFormValuesFromURL";
import { trackCustomEvent } from "../../utils/analytics";
import type { FormValues } from "./Serp";

interface Props {
  location: PageProps["location"];
  tx: Queries.SERPPageQuery["tx"];
  handleSubmit: UseFormHandleSubmit<FormValues>;
  onSubmit: (data: FormValues) => void;
  register: UseFormRegister<FormValues>;
  values: any;
}

export default function SearchForm({
  location,
  tx,
  handleSubmit,
  onSubmit,
  register,
  values,
}: Props) {
  const [{ isCollapsed, setFocusOn }, toggleCollapse] = useState<{
    isCollapsed: boolean;
    setFocusOn: string | null;
  }>({
    isCollapsed: true,
    setFocusOn: null,
  });

  useFormValuesFromURL(location, (searchParams: AllowedSearchParams) => {
    toggleCollapse({
      isCollapsed: !!searchParams.position,
      setFocusOn: null,
    });
  });

  return (
    <div className="bg-slate-100 py-4 shadow-inner sm:py-8 ">
      {isCollapsed && (
        <div className="flex items-center px-4 sm:justify-center">
          <h1
            className="flex flex-wrap items-center gap-2 px-4 sm:justify-center"
            data-cy="serp-search-form"
          >
            <span className="hidden sm:block">{tx!.found}</span>{" "}
            <button
              aria-label={tx!.positionLabel!}
              className="inline-flex cursor-pointer items-center rounded-lg border-gray-500 bg-white py-1 pl-2 pr-2 text-primary-600 focus:border-primary-600 focus:ring-primary-600"
              onClick={() => {
                toggleCollapse({
                  isCollapsed: false,
                  setFocusOn: "position",
                });
                trackCustomEvent({
                  action: "click",
                  category: "SERP",
                  label: "Open search form",
                });
              }}
            >
              <MagnifyingGlassIcon
                className="mr-1 h-5 w-5"
                aria-hidden="true"
              />
              {values.position}{" "}
            </button>
            {tx!.jobs}
            {values.location && (
              <>
                {` ${tx!.in} `}
                <button
                  aria-label={tx!.locationLabel!}
                  className="inline-flex cursor-pointer items-center justify-center rounded-lg border-gray-500 bg-white py-1 pl-2 pr-2 text-primary-600"
                  onClick={() => {
                    toggleCollapse({
                      isCollapsed: false,
                      setFocusOn: "location",
                    });
                    trackCustomEvent({
                      action: "click",
                      category: "SERP",
                      label: "Open search form",
                    });
                  }}
                >
                  <MapPinIcon className="mr-1 h-5 w-5" aria-hidden="true" />
                  {values.location}
                </button>
              </>
            )}
          </h1>
        </div>
      )}
      {!isCollapsed && (
        <form
          onSubmit={(e) => {
            toggleCollapse({ isCollapsed: true, setFocusOn: null });
            handleSubmit(onSubmit)(e);
            trackCustomEvent({
              action: "submit",
              category: "SERP",
              label: "Submit search form",
            });
          }}
          method="get"
          action={location.pathname}
          className="mx-auto max-w-2xl px-8 sm:px-20"
        >
          <fieldset className="mb-4 sm:flex sm:justify-between sm:gap-4">
            <div className="relative mt-1 mb-4 rounded-md shadow-sm sm:mb-0 sm:grow">
              <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center">
                <MagnifyingGlassIcon className="h-5 w-5" aria-hidden="true" />
              </div>
              <input
                id="position"
                autoFocus={setFocusOn === "position"}
                type="text"
                className={cx(
                  "block w-full rounded-md border-gray-300 py-3 pl-10 pr-4 shadow-sm focus:border-primary-600 focus:ring-primary-600"
                )}
                placeholder={tx!.positionLabel}
                required
                {...register("position", { required: true })}
              />
            </div>
            <div className="relative mt-1 rounded-md shadow-sm sm:grow">
              <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center">
                <MapPinIcon className="h-5 w-5" aria-hidden="true" />
              </div>
              <input
                id="location"
                type="text"
                autoFocus={setFocusOn === "location"}
                className={
                  "block w-full rounded-md border-gray-300 py-3 pl-10 pr-4 shadow-sm focus:border-primary-600 focus:ring-primary-600"
                }
                placeholder={tx!.locationLabel}
                {...register("location", { maxLength: 20 })}
              />
            </div>
          </fieldset>
          <button
            type="submit"
            className="block w-full rounded-md border border-transparent bg-primary-600 px-5 py-3 text-base font-medium text-white shadow hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-primary-600 sm:px-10"
          >
            {tx!.continueLabel}
          </button>
        </form>
      )}
    </div>
  );
}
